import React from 'react';
import styled from 'styled-components';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

// A coordinate box with ▲▼ steppers, shared by the Inspection Form Builder
// (millimetres) and the Form Setup placeholder editor (view pixels).
//
// The arrows are SPATIAL nudges, not numeric spinners: ▲ always moves the field
// toward the top / right of the page. Because Y grows downward, ▲ on a Y box
// SUBTRACTS from the stored value. That inversion is subtle enough that the two
// editors must not each carry their own copy of it.
const AXIS_MOVES = {
  x: { up: 'right', down: 'left', sign: 1 },
  y: { up: 'up', down: 'down', sign: -1 },
};

// One decimal keeps stored values tidy at the granularity a user could type.
const round1 = (v: number): number => Math.round(v * 10) / 10;

interface Props {
  axis: 'x' | 'y';
  label: string;
  value: any;
  onChange: (v: string) => void;
  /** Distance one arrow press moves the field. */
  step?: number;
  /** Distance with Shift held. */
  coarseStep?: number;
  /** Unit shown in the arrow tooltips, e.g. 'mm' or 'px'. */
  unit?: string;
}

const CoordInput = ({
  axis,
  label,
  value,
  onChange,
  step: stepSize = 1,
  coarseStep = 10,
  unit = 'mm',
}: Props) => {
  const moves = AXIS_MOVES[axis];

  // `direction` is +1 for the ▲ button; the axis sign turns that into the right
  // arithmetic for the coordinate space.
  const step = (direction: number, coarse: boolean) => {
    const current = Number(value);
    const base = Number.isFinite(current) ? current : 0;
    const delta = direction * moves.sign * (coarse ? coarseStep : stepSize);
    onChange(`${round1(base + delta)}`);
  };

  const hint = (way: string) =>
    `Move ${way} ${stepSize} ${unit} (hold Shift for ${coarseStep} ${unit})`;

  return (
    <Field>
      <Lbl>{label}</Lbl>
      <Stepper>
        <StepperInput
          value={value ?? ''}
          inputMode="decimal"
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            // Otherwise the browser's caret-to-end behaviour fires as well.
            e.preventDefault();
            step(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey);
          }}
        />
        <Arrows>
          {/* tabIndex -1: tabbing should land on the next box, not the steppers. */}
          <ArrowBtn
            type="button"
            tabIndex={-1}
            title={hint(moves.up)}
            aria-label={`Move ${moves.up}`}
            onClick={e => step(1, e.shiftKey)}
          >
            <ArrowDropUpIcon style={{ fontSize: 16 }} />
          </ArrowBtn>
          <ArrowBtn
            type="button"
            tabIndex={-1}
            title={hint(moves.down)}
            aria-label={`Move ${moves.down}`}
            onClick={e => step(-1, e.shiftKey)}
          >
            <ArrowDropDownIcon style={{ fontSize: 16 }} />
          </ArrowBtn>
        </Arrows>
      </Stepper>
    </Field>
  );
};

export default CoordInput;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Lbl = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const Stepper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// Room on the right for the arrow column so a long value never slides under it.
const StepperInput = styled.input`
  height: 36px;
  padding: 0 28px 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Arrows = styled.div`
  position: absolute;
  top: 3px;
  right: 3px;
  bottom: 3px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const ArrowBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  min-height: 0;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }

  &:active {
    background: var(--primary-100);
  }
`;
